import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminLayout } from './components/AdminLayout';
import { Layout } from './components/Layout';
import { ApiError, api, type Me } from './lib/api';
import { AuditTrail } from './routes/AuditTrail';
import { Billing } from './routes/Billing';
import { DriverDetail } from './routes/DriverDetail';
import { Fleet } from './routes/Fleet';
import { Login } from './routes/Login';
import { ReviewQueue } from './routes/ReviewQueue';
import { Activity } from './routes/admin/Activity';
import { CustomerDetail } from './routes/admin/CustomerDetail';
import { Customers } from './routes/admin/Customers';

export function App() {
  const { data, isPending, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/auth/me'),
    // A 401 is the expected answer for a signed-out visitor, not a failure to retry.
    retry: (_count, err) => !(err instanceof ApiError && err.status === 401),
  });

  if (isPending) {
    return (
      <div className="boot">
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  const signedIn = !!data && !error;

  if (!signedIn) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // A driver who reaches the carrier app has the wrong app; the PWA is a separate build.
  if (data.user.role === 'driver') {
    return (
      <div className="boot">
        <p className="prose" style={{ maxWidth: '40ch', textAlign: 'center' }}>
          This is the carrier workspace. Drivers upload documents from the driver app —
          check the link in your invite.
        </p>
      </div>
    );
  }

  /*
   * Milepost staff get the platform console — unless they are impersonating, in which case
   * the session carries a carrier's tenant and they fall through to the carrier app below.
   * That fall-through IS the feature: support should see the workspace the carrier sees,
   * not a Milepost-flavoured rendering of it that could differ in exactly the way being
   * reported. The impersonation banner in Layout is what keeps the two apart on screen.
   */
  if (data.user.role === 'platform_admin' && !data.user.tenantId) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<AdminLayout me={data} />}>
          <Route path="/" element={<Customers />} />
          <Route path="/customers/:id" element={<CustomerDetail />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<Layout me={data} />}>
        <Route path="/" element={<Fleet />} />
        <Route path="/drivers/:id" element={<DriverDetail />} />
        <Route path="/review" element={<ReviewQueue />} />
        <Route path="/audit" element={<AuditTrail />} />
        <Route path="/billing" element={<Billing role={data.user.role} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
